import { ApiClient } from '../utils/apiClient';
import { registerUserScopedCache } from './dedupedFetch';

export interface ConsultantRoleConfig {
  [jobTitle: string]: boolean;
}

export type ConsultantRoleState = 'CONSULTANT' | 'NOT_CONSULTANT' | 'UNCLASSIFIED';

export interface RoleConfigWithCount {
  role: string;
  state: ConsultantRoleState;
  /** `true` only for a saved consultant role — never the suggestion. */
  isConsultant: boolean;
  /** A guess from the title's wording, for pre-selecting a choice. Never authoritative. */
  suggestedIsConsultant: boolean;
  /** Everybody holding the title, whatever their status. */
  count: number;
  /** Holders who are active or hired. */
  activeCount: number;
}

/**
 * Service for managing consultant role configurations
 * Provides API-based database storage and caching
 */
class ConsultantRoleService {
  private cache: ConsultantRoleConfig | null = null;
  private cacheTimestamp: number = 0;
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  /**
   * Shared in-flight promise so that parallel callers (e.g. N rows in a
   * filter callback) share a single HTTP request instead of each issuing
   * one before the cache has been populated.
   */
  private inFlight: Promise<ConsultantRoleConfig> | null = null;
  /** Bumped by `clearCache()`, so a request in flight then cannot refill the cache. */
  private generation = 0;
  
  /**
   * Get the current consultant role configuration from API
   */
  async getConsultantRoleConfig(apiClient: ApiClient): Promise<ConsultantRoleConfig> {
    if (this.cache && (Date.now() - this.cacheTimestamp) < this.CACHE_TTL) {
      return this.cache;
    }

    if (this.inFlight) {
      return this.inFlight;
    }

    const generation = this.generation;
    this.inFlight = (async () => {
      try {
        const response = await apiClient.get('/api/consultant-roles/config');
        const config = response.config || {};

        if (generation === this.generation) {
          this.cache = config;
          this.cacheTimestamp = Date.now();
        }

        return config;
      } catch (error) {
        console.error('Failed to fetch consultant role config:', error);
        return {};
      } finally {
        if (generation === this.generation) this.inFlight = null;
      }
    })();

    return this.inFlight;
  }

  /**
   * Get consultant role configuration with employee counts
   */
  /**
   * The classification gap the server counts beside the role list: the
   * unclassified titles someone active or hired holds, and those people.
   */
  async getUnclassifiedHeld(apiClient: ApiClient): Promise<UnclassifiedSummary> {
    const response = await apiClient.get('/api/consultant-roles');
    return response.summary?.unclassifiedHeld ?? { titles: 0, people: 0 };
  }

  async getConsultantRoleConfigWithCounts(apiClient: ApiClient): Promise<RoleConfigWithCount[]> {
    try {
      const response = await apiClient.get('/api/consultant-roles');
      return response.roles || [];
    } catch (error) {
      console.error('Failed to fetch consultant role config with counts:', error);
      throw error;
    }
  }

  /**
   * Merge classification changes into the saved configuration. Only the titles
   * named are touched; the server keeps every other saved key.
   */
  async updateConsultantRoleConfig(
    apiClient: ApiClient,
    changes: ConsultantRoleConfig
  ): Promise<void> {
    try {
      await apiClient.put('/api/consultant-roles', { changes });
      
      this.cache = null;
      this.cacheTimestamp = 0;
      this.inFlight = null;
    } catch (error) {
      console.error('Failed to update consultant role config:', error);
      throw error;
    }
  }

  /**
   * Check if a specific job title is configured as a consultant role
   */
  async isConsultantRole(apiClient: ApiClient, jobTitle: string): Promise<boolean> {
    if (!jobTitle || jobTitle.trim() === '') {
      return false;
    }

    const config = await this.getConsultantRoleConfig(apiClient);
    return isConsultantRoleSync(jobTitle, config);
  }

  /**
   * Get all job titles that are configured as consultant roles
   */
  async getConsultantJobTitles(apiClient: ApiClient): Promise<string[]> {
    try {
      const response = await apiClient.get('/api/consultant-roles/consultant-titles');
      return response.consultantJobTitles || [];
    } catch (error) {
      console.error('Failed to fetch consultant job titles:', error);
      return [];
    }
  }

  /**
   * Clear the cache to force a fresh fetch
   */
  clearCache(): void {
    this.generation += 1;
    this.cache = null;
    this.cacheTimestamp = 0;
    this.inFlight = null;
  }
}

// Export singleton instance
export const consultantRoleService = new ConsultantRoleService();
registerUserScopedCache({ clear: () => consultantRoleService.clearCache() });

export interface UnclassifiedSummary {
  /** Unclassified titles that an active or hired employee holds. */
  titles: number;
  /** The active or hired people holding them. */
  people: number;
}

/**
 * Whether a job title is a SAVED consultant role, against a preloaded config.
 *
 * A title with no saved key is unclassified and is not a consultant role —
 * the same answer the server gives on every path. There is deliberately no
 * wording-based fallback here: a guess on this side would put somebody on the
 * Dashboard's consultant count while the Planning Board leaves them out. The
 * authority is `resolveRoleState` in the API; this only reads what it stored,
 * comparing trimmed as it does.
 *
 * Synchronous so a filter callback can classify many rows in one render.
 */
export function isConsultantRoleSync(
  jobTitle: string,
  config: ConsultantRoleConfig | null | undefined,
): boolean {
  const wanted = jobTitle?.trim();
  if (!wanted || !config) return false;

  if (config[wanted] !== undefined) return config[wanted] === true;
  return Object.entries(config).some(([key, value]) => key.trim() === wanted && value === true);
}
